import AmericanCompanyFundamentalsChart from "components/company/financials/american/fundamentals/AmericanCompanyFundamentalsChart";
import ReitFinancials from "components/company/financials/american/ReitFinancials";
import YearlyQuarterlySwitch from "components/company/financials/YearlyQuarterlySwitch";
import FooterDataDisclaimer from "components/company/FooterDataDisclaimer";
import getQuarter from "date-fns/getQuarter";
import { useAmplitude } from "lib/amplitude/Amplitude";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import Link from "next/link";
import { useState } from "react";
import { Value } from "react-powerplug";
import useSelfie from "../../../../lib/hooks/useSelfie";
import YearlyBankFinancials from "./BankFinancials";
import CompanyFinancials from "./CompanyFinancials";
import InsurerFinancials from "./InsurerFinancials";

interface IAmericanCompanyFinancialsProps {
  company: IAmericanCompanyIssuerTopLevelFragment;
}

export default function AmericanCompanyFinancials({ company }: IAmericanCompanyFinancialsProps) {
  const user = useSelfie();
  const amplitude = useAmplitude();
  const [filterByReportDate, setFilterByReportDate] = useState<Date | null>(null);

  if (!user) {
    return null;
  }

  const linkToPremium = (
    <Link
      href="/premium"
      onClick={() => {
        amplitude.logEvent("Click on link to become Premium", {
          origin: "Must be premium to view updated financials",
        });
      }}
      className="font-bold text-blue-600 hover:underline"
    >
      Premium
    </Link>
  );

  const isBank = company.financialsTemplate.templateCode === "B";
  const isInsurer = company.financialsTemplate.templateCode === "I";
  const isREIT = company.isREIT && !isBank && !isInsurer;
  const isCompany = !isBank && !isInsurer && !isREIT;

  // If the user is Premium and we only have Quarterly financials, display the Quarterly data by default
  let defaultSelectedFinancials: "YEARLY" | "QUARTERLY" = "YEARLY";
  if (company.modernFinancials.yearly.length === 0 && user.isPremium && company.modernFinancials.quarterly.length > 0) {
    defaultSelectedFinancials = "QUARTERLY";
  }

  return (
    <Value<"YEARLY" | "QUARTERLY">
      initial={defaultSelectedFinancials}
      onChange={(period) => {
        amplitude.logEvent("Toggle financials", {
          tickerSymbol: company.primaryTickerSymbol,
          period,
        });
      }}
    >
      {({ value, set }) => {
        let financials = value === "QUARTERLY" ? company.modernFinancials.quarterly : company.modernFinancials.yearly;

        if (value === "QUARTERLY" && filterByReportDate) {
          financials = financials.filter((candidate) => {
            return getQuarter(filterByReportDate) === getQuarter(parseDateYmd(candidate.reportDate));
          });
        }

        return (
          <>
            <div className="mx-auto flex flex-col items-center">
              {user.isPremium && (
                <div className="my-8 text-center">
                  <YearlyQuarterlySwitch onChange={set} value={value} />
                </div>
              )}

              <div
                className="flex w-max"
                css={{
                  zIndex: 2,
                  position: "relative",
                  backgroundColor: "#ffffff",
                  borderRadius: 4,
                  boxShadow: "0 4px 6px 0 #33333333",
                }}
              >
                {isCompany && (
                  <CompanyFinancials
                    displayAllColumns={user.isPremium}
                    financials={financials}
                    onFilterByQuarter={(reportDate) => setFilterByReportDate(filterByReportDate ? null : reportDate)}
                  />
                )}
                {isBank && (
                  <YearlyBankFinancials
                    displayAllColumns={user.isPremium}
                    financials={financials}
                    onFilterByQuarter={(reportDate) => setFilterByReportDate(filterByReportDate ? null : reportDate)}
                  />
                )}
                {isInsurer && (
                  <InsurerFinancials
                    displayAllColumns={user.isPremium}
                    financials={financials}
                    onFilterByQuarter={(reportDate) => setFilterByReportDate(filterByReportDate ? null : reportDate)}
                  />
                )}
                {isREIT && (
                  <ReitFinancials
                    displayAllColumns={user.isPremium}
                    financials={financials}
                    onFilterByQuarter={(reportDate) => setFilterByReportDate(filterByReportDate ? null : reportDate)}
                  />
                )}
              </div>

              {financials.length > 0 && (
                <div className="mt-4 flex justify-center">
                  <FooterDataDisclaimer>
                    {!user.isPremium ? (
                      <>
                        <strong>In order to see the updated quarterly values you must be {linkToPremium}!</strong> The
                        values above are represented in USD and the scale is in millions—10.000 is the equivalent of 10
                        billions. <strong>L</strong> inside the table means Loss.{" "}
                      </>
                    ) : (
                      <>
                        The values above are represented in USD and the scale is in millions—10.000 is the equivalent of
                        10 billions. <strong>L</strong> inside the table means Loss.
                      </>
                    )}
                  </FooterDataDisclaimer>
                </div>
              )}

              {company.modernFinancials.yearly.length > 12 && user.isPremium && (
                <div className="mt-8 text-center">
                  <YearlyQuarterlySwitch onChange={set} value={value} />
                </div>
              )}
            </div>

            {financials.length > 0 && <AmericanCompanyFundamentalsChart company={company} financials={financials} />}
          </>
        );
      }}
    </Value>
  );
}
